<template>
  <AppDialog
    :model-value="modelValue"
    size="lg"
    content-class="global-search-modal global-search-modal--top"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <!-- El buscador es la cabecera: no hay título ni icono que poner. -->
    <template #header>
      <div class="global-search-modal__header">
        <v-text-field
          ref="inputRef"
          v-model="query"
          :placeholder="t('search.placeholder')"
          density="compact"
          hide-details
          variant="solo"
          flat
          prepend-inner-icon="mdi-magnify"
          clearable
          class="global-search-modal__input"
          autofocus
          @update:model-value="onSearchInput"
        />
        <v-btn
          icon
          variant="text"
          class="global-search-modal__close"
          :aria-label="t('search.close')"
          @click="$emit('update:modelValue', false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </template>

    <v-divider />

      <!-- Cuerpo: estado vacío, buscando, sin resultados o resultados -->
      <div class="global-search-modal__body">
        <!-- Estado vacío: no hay query o < 2 caracteres -->
        <div
          v-if="(query || '').trim().length < 2"
          class="global-search-modal__empty"
        >
          <v-icon size="96" class="global-search-modal__empty-icon">mdi-magnify</v-icon>
          <p class="global-search-modal__empty-title">{{ t('search.empty_title') }}</p>
          <p class="global-search-modal__empty-hint">{{ t('search.empty_hint') }}</p>
        </div>

        <!-- Cargando -->
        <div v-else-if="loading" class="global-search-modal__loading">
          <v-progress-circular indeterminate size="32" />
          <span class="ml-3">{{ t('search.loading') }}</span>
        </div>

        <!-- Sin resultados -->
        <div
          v-else-if="!hasResults()"
          class="global-search-modal__empty"
        >
          <v-icon size="96" class="global-search-modal__empty-icon">mdi-magnify</v-icon>
          <p class="global-search-modal__empty-title">{{ t('search.no_results', { q: (query || '').trim() }) }}</p>
          <p class="global-search-modal__empty-hint">{{ t('search.try_other') }}</p>
        </div>

        <!-- Resultados agrupados -->
        <div v-else-if="hasResults()" class="global-search-modal__results">
          <div v-if="results.tvshows.length > 0" class="global-search-modal__group">
            <div class="global-search-modal__group-title global-search-modal__group-title--series">
              <v-icon size="18" icon="mdi-television" />
              <span>{{ t('search.group_series', { n: results.tvshows.length }) }}</span>
            </div>
            <GlobalSearchResultCard
              v-for="item in results.tvshows"
              :key="'tvshow-' + item.id"
              :item="item"
              @select="onSelect(item)"
            />
          </div>
          <div v-if="results.movies.length > 0" class="global-search-modal__group">
            <div class="global-search-modal__group-title global-search-modal__group-title--movies">
              <v-icon size="18" icon="mdi-movie-open" />
              <span>{{ t('search.group_movies', { n: results.movies.length }) }}</span>
            </div>
            <GlobalSearchResultCard
              v-for="item in results.movies"
              :key="'movie-' + item.id"
              :item="item"
              @select="onSelect(item)"
            />
          </div>
          <div v-if="results.shows.length > 0" class="global-search-modal__group">
            <div class="global-search-modal__group-title global-search-modal__group-title--shows">
              <v-icon size="18" icon="mdi-theater" />
              <span>{{ t('search.group_shows', { n: results.shows.length }) }}</span>
            </div>
            <GlobalSearchResultCard
              v-for="item in results.shows"
              :key="'show-' + item.id"
              :item="item"
              @select="onSelect(item)"
            />
          </div>
        </div>
      </div>
  </AppDialog>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import AppDialog from '@/components/ui/AppDialog.vue'
import { useGlobalSearch } from '@/composables/useGlobalSearch'
import GlobalSearchResultCard from '@/components/shared/GlobalSearchResultCard.vue'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'select'])

const inputRef = ref(null)
const { query, loading, results, search, clear, hasResults } = useGlobalSearch()

watch(() => props.modelValue, (open) => {
  if (open) {
    nextTick(() => inputRef.value?.focus())
  } else {
    clear()
  }
})

function onSearchInput(value) {
  const normalized = value ?? ''
  query.value = normalized
  search(normalized)
}

function onSelect(item) {
  emit('select', item)
  emit('update:modelValue', false)
}
</script>

<style scoped>
.global-search-modal__card {
  background: rgb(var(--v-theme-surface));
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  border: 1px solid rgb(39, 38, 43) !important;
  border-radius: 16px !important;
  overflow: hidden;
}
.global-search-modal__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
}
.global-search-modal__input {
  flex: 1;
}
.global-search-modal__input :deep(.v-field) {
  background: rgb(9, 9, 11) !important;
  border: none !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}
.global-search-modal__input :deep(.v-field__outline),
.global-search-modal__input :deep(.v-field__overlay) {
  display: none !important;
}
.global-search-modal__input :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.9);
}
.global-search-modal__input :deep(input::placeholder) {
  color: rgba(255, 255, 255, 0.45);
}
.global-search-modal__close {
  flex-shrink: 0;
}
.global-search-modal__body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 20px 24px;
  min-height: 200px;
}
.global-search-modal__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}
.global-search-modal__empty-icon {
  margin-bottom: 20px;
  color: rgb(55, 55, 60) !important;
  opacity: 1;
}
.global-search-modal__empty-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgb(75, 75, 80);
  margin: 0 0 6px;
}
.global-search-modal__empty-hint {
  font-size: 0.8125rem;
  color: rgb(55, 55, 60);
  margin: 0;
}
.global-search-modal__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: rgb(75, 75, 80);
}
.global-search-modal__results {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.global-search-modal__group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.global-search-modal__group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(var(--v-theme-on-surface), 0.7);
  padding: 8px 4px 4px;
}
.global-search-modal__group-title--series :deep(.v-icon) {
  color: rgb(194, 122, 255) !important;
}
.global-search-modal__group-title--movies :deep(.v-icon) {
  color: rgb(250, 101, 182) !important;
}
.global-search-modal__group-title--shows :deep(.v-icon) {
  color: rgb(81, 163, 255) !important;
}
</style>

<style>
/* Posicionar la modal pegada a la parte superior y borde + redondeado visibles */
.global-search-modal--top {
  align-self: flex-start !important;
  margin-top: 80px !important;
  border: 1px solid rgb(39, 38, 43) !important;
  border-radius: 16px !important;
  overflow: hidden;
}
</style>
